import apiV2 from "api-v2";
import type {
	AwardedProject,
	AwardedSupplierListResponse,
	CreateRatingPayload,
	KpiCategory,
	MySuppliersFilters,
	SupplierPerformanceDetail,
	SupplierRating,
	UpdateDraftPayload,
} from "../types/supplier-performance.types";

const buildFilterParams = (filters: MySuppliersFilters): Record<string, unknown> => {
	const params: Record<string, unknown> = {
		page: filters.page,
		per_page: filters.perPage,
		sort_by: filters.sortBy,
		sort_order: filters.sortOrder,
	};

	if (filters.search) {
		params.search = filters.search;
	}

	if (filters.supplierType.length > 0) {
		params.supplier_type = filters.supplierType.join(",");
	}

	if (filters.performanceBand.length > 0) {
		params.performance_band = filters.performanceBand.join(",");
	}

	if (filters.isActive !== null) {
		params.is_active = filters.isActive;
	}

	return params;
};

export const getMySuppliers = async (
	companyId: number,
	filters: MySuppliersFilters
): Promise<AwardedSupplierListResponse> => {
	const params = buildFilterParams(filters);
	const { data } = await apiV2.get(`companies/${companyId}/my-suppliers`, { params });
	return data;
};

export const getSupplierPerformanceDetail = async (
	companyId: number,
	supplierId: number
): Promise<SupplierPerformanceDetail> => {
	const { data } = await apiV2.get(
		`companies/${companyId}/my-suppliers/${supplierId}`
	);
	return data.data;
};

export const getSupplierEngagements = async (
	companyId: number,
	supplierId: number
): Promise<AwardedProject[]> => {
	const { data } = await apiV2.get(
		`companies/${companyId}/my-suppliers/${supplierId}/engagements`
	);
	return data.data;
};

export const getAwardedProjects = async (
	companyId: number,
	supplierId: number
): Promise<AwardedProject[]> => {
	const { data } = await apiV2.get(
		`companies/${companyId}/my-suppliers/${supplierId}/awarded-projects`
	);
	return data.data;
};

export const getLatestRating = async (
	companyId: number,
	supplierId: number,
	projectId?: number | null,
	rfxId?: number | null
): Promise<SupplierRating | null> => {
	const params: Record<string, unknown> = {};
	if (projectId) {
		params.project_id = projectId;
	}
	if (rfxId) {
		params.rfx_id = rfxId;
	}
	const { data } = await apiV2.get(
		`companies/${companyId}/my-suppliers/${supplierId}/latest-rating`,
		{ params }
	);
	return data.data;
};

export const getPerformanceCategories = async (): Promise<KpiCategory[]> => {
	const { data } = await apiV2.get("performance/categories");
	return data.data;
};

export const createRating = async (
	payload: CreateRatingPayload
): Promise<SupplierRating> => {
	const { data } = await apiV2.post(
		"performance-ratings",
		payload
	);
	return data.data;
};

export const updateDraft = async (
	ratingId: number,
	payload: UpdateDraftPayload
): Promise<SupplierRating> => {
	const { data } = await apiV2.patch(
		`performance-ratings/${ratingId}`,
		payload
	);
	return data.data;
};

export const deleteRating = async (
	ratingId: number
): Promise<void> => {
	await apiV2.delete(`performance-ratings/${ratingId}`);
};

export const selectCategories = async (
	ratingId: number,
	categoryIds: number[]
): Promise<SupplierRating> => {
	const { data } = await apiV2.put(
		`performance-ratings/${ratingId}/categories`,
		{ category_ids: categoryIds }
	);
	return data.data;
};

export const saveScores = async (
	ratingId: number,
	scores: Array<{ sub_criteria_id: number; score: number }>
): Promise<SupplierRating> => {
	const { data } = await apiV2.put(
		`performance-ratings/${ratingId}/scores`,
		{ scores }
	);
	return data.data;
};

export const saveComments = async (
	ratingId: number,
	comments: Array<{ sub_criteria_id: number; comment: string | null }>
): Promise<SupplierRating> => {
	const { data } = await apiV2.put(
		`performance-ratings/${ratingId}/comments`,
		{ comments }
	);
	return data.data;
};

export const submitRating = async (
	ratingId: number
): Promise<SupplierRating> => {
	const { data } = await apiV2.post(
		`performance-ratings/${ratingId}/submit`
	);
	return data.data;
};

export const exportSupplierPdf = async (
	companyId: number,
	supplierId: number
): Promise<Blob> => {
	const { data } = await apiV2.get(
		`companies/${companyId}/my-suppliers/${supplierId}/export-pdf`,
		{ responseType: "blob" }
	);
	return data;
};

export const exportRatingPdf = async (
	ratingId: number
): Promise<Blob> => {
	const { data } = await apiV2.get(
		`performance-ratings/${ratingId}/export-pdf`,
		{ responseType: "blob" }
	);
	return data;
};
